import { useContext, useState } from 'react';
import { SelectGenre } from './Select/SelectGenre';
import { SelectInstrument } from './Select/SelectInstrument';
import { createOrUpdateCard } from '../utils/createOrUpdateCard';
import { deleteData } from '../utils/deleteData';
import { MdDelete, MdEdit, MdSave } from 'react-icons/md';
import { getLocalAvatar, resolveAvatarUrl } from '../utils/avatarUtils';
import { API_BASE_URL } from '../utils/apiBaseUrl';
import { GlobalContext } from '../context/GlobalState';
import { uploadImage } from '../utils/uploadImage';

export const MyMusicianCard = ({ musician }) => {
  const { loadMusicians, showNotification } = useContext(GlobalContext);
  const {
    cardId,
    firstName,
    lastName,
    instrument,
    genre,
    description,
    image_url,
    uploadedImageId,
  } = musician;

  const [isEditing, setIsEditing] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [imageError, setImageError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [editedData, setEditedData] = useState({
    firstName,
    lastName,
    instrument,
    genre,
    description,
  });

  const handleEdit = () => {
    setPreviewImageUrl(
      resolveAvatarUrl(image_url, `${firstName} ${lastName}`, uploadedImageId),
    );
    setImageError('');
    setSelectedImage(null);
    setIsEditing(true);
  };
  const handleDelete = async () => {
    try {
      await deleteData(`${API_BASE_URL}/musicians/${cardId}`);
      await loadMusicians(true);
      showNotification('Musician card deleted.', 'warning');
    } catch (error) {
      showNotification('Could not delete musician card.', 'error');
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    const { firstName, lastName, instrument, genre, description } = editedData;
    let nextImageUrl = image_url;
    let nextUploadedImageId = uploadedImageId || null;

    try {
      if (selectedImage) {
        const uploadedImage = await uploadImage(selectedImage);
        nextImageUrl = uploadedImage.image_url;
        nextUploadedImageId = uploadedImage.uploadedImageId;
      }

      await createOrUpdateCard(
        `${API_BASE_URL}/musicians/${cardId}`,
        {
          firstName,
          lastName,
          instrument,
          genre,
          description,
          image_url: nextImageUrl,
          uploadedImageId: nextUploadedImageId,
        },
        'PATCH',
      );
      await loadMusicians(true);
      setIsEditing(false);
      setSelectedImage(null);
      setImageError('');
      showNotification('Musician card updated.', 'success');
    } catch (error) {
      setImageError('Image upload failed. Please try again.');
      showNotification('Could not update musician card.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (fieldName, value) => {
    setEditedData(prevData => ({
      ...prevData,
      [fieldName]: value,
    }));
  };

  const handleImageChange = event => {
    const file = event.target.files?.[0];

    if (!file) {
      setSelectedImage(null);
      setPreviewImageUrl(
        resolveAvatarUrl(
          image_url,
          `${firstName} ${lastName}`,
          uploadedImageId,
        ),
      );
      return;
    }

    if (!file.type.startsWith('image/')) {
      setImageError('Please select a valid image file.');
      return;
    }

    setImageError('');
    setSelectedImage(file);
    setPreviewImageUrl(URL.createObjectURL(file));
  };

  return (
    <li className='card card-musician'>
      {!isEditing && (
        <img
          src={resolveAvatarUrl(
            image_url,
            `${firstName} ${lastName}`,
            uploadedImageId,
          )}
          alt='musician'
          loading='lazy'
          decoding='async'
          onError={e => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = getLocalAvatar(`${firstName} ${lastName}`);
          }}
        />
      )}
      {isEditing ? (
        <>
          <div className='form-item'>
            <label htmlFor={`musician-photo-${cardId}`}>Card Photo</label>
            <div className='form-image-preview-wrap'>
              <img
                className='form-image-preview'
                src={
                  previewImageUrl ||
                  resolveAvatarUrl(
                    image_url,
                    `${firstName} ${lastName}`,
                    uploadedImageId,
                  )
                }
                alt='Musician card preview'
              />
            </div>
            <input
              id={`musician-photo-${cardId}`}
              type='file'
              accept='image/*'
              onChange={handleImageChange}
            />
            {imageError && <span className='message-error'>{imageError}</span>}
          </div>
          <input
            type='text'
            placeholder='First name'
            value={editedData.firstName}
            onChange={e => handleInputChange('firstName', e.target.value)}
          />
          <input
            type='text'
            placeholder='Last name'
            value={editedData.lastName}
            onChange={e => handleInputChange('lastName', e.target.value)}
          />
          <SelectInstrument
            handleInputChange={handleInputChange}
            data={editedData}
          />
          <SelectGenre
            handleInputChange={handleInputChange}
            data={editedData}
          />
          <input
            type='text'
            placeholder='Tell something about yourself'
            value={editedData.description}
            onChange={e => handleInputChange('description', e.target.value)}
          />
        </>
      ) : (
        <>
          <div className='card-title'>
            {firstName} {lastName}
          </div>
          <div className='card-divider' />
          <div className='card-meta'>Genre: {genre}</div>
          <div className='card-section'>
            <span className='card-section-label'>Instrument</span>
            {instrument.map(element => (
              <span className='chip instrument' key={element}>
                {element}
              </span>
            ))}
          </div>
          <div className='card-description'>{description}</div>
        </>
      )}

      <div className='fav-apply-container'>
        {isEditing ? (
          <button className='action-button action-save' onClick={handleSave}>
            <MdSave />
            <span>{isSaving ? 'Saving...' : 'Save'}</span>
          </button>
        ) : (
          <div className='container-card-bottom'>
            <button className='action-button' title='edit' onClick={handleEdit}>
              <MdEdit />
              <span>Edit</span>
            </button>
            <button
              title='delete'
              className='action-button button-delete'
              onClick={handleDelete}
            >
              <MdDelete />
              <span>Delete</span>
            </button>
          </div>
        )}
      </div>
    </li>
  );
};
